import {
  getInvoiceAmountsBreakdown,
  getPubDateAndPublicationLineItemsDifference
} from 'lib/pricing';
import { isPublicNoticeInvoice } from 'lib/types/invoices';
import api from 'api';
import { ColumnService } from 'lib/services/directory';
import { logAndCaptureException } from 'utils';
import {
  RefundUIAdapter,
  RefundUIConfig,
  RefundUIAdapterArgs,
  PerformRefundContext
} from '../types';

export const noticeRefundAdapter: RefundUIAdapter = {
  getLineItemsDiff({ entity, invoice }: RefundUIAdapterArgs) {
    if (entity.kind !== 'notice' || !isPublicNoticeInvoice(invoice)) return [];
    try {
      const { publicationLineItemDiffs, pubDateDiffs } =
        getPubDateAndPublicationLineItemsDifference(
          invoice,
          entity.publicationDates || []
        );
      if (
        publicationLineItemDiffs.length > 0 &&
        (pubDateDiffs ?? []).length === 0
      ) {
        return publicationLineItemDiffs;
      }
    } catch (e) {
      // swallow for now
    }
    return [];
  },
  getDefaultRefundAmount(args: RefundUIAdapterArgs): number {
    const { entity, invoice } = args;
    if (entity.kind !== 'notice') return 0;
    const diffs = noticeRefundAdapter.getLineItemsDiff(args);
    if (diffs.length > 0 && entity.partialRefundAfterEdit) {
      const refundAmountWithoutTaxes = diffs.reduce(
        (acc, li) => acc + li.amount,
        0
      );
      const {
        subtotalInCents,
        columnAmountInCents,
        taxesInCents,
        noticeAddOnsInCents
      } = getInvoiceAmountsBreakdown(invoice);
      // Add-ons are a delivered service, so they scale with neither the removed line items nor their fees
      const proportionOfSubtotal =
        refundAmountWithoutTaxes / (subtotalInCents - noticeAddOnsInCents);
      const proportionateTaxesAndFees =
        proportionOfSubtotal *
        (taxesInCents + columnAmountInCents - noticeAddOnsInCents);
      return (refundAmountWithoutTaxes + proportionateTaxesAndFees) / 100;
    }
    return 0;
  },
  buildUIConfig({ permissions }): RefundUIConfig {
    const { canRefund, canVoid } = permissions;
    const refundDisabled = !(canRefund || canVoid);
    return {
      header: 'Refund Invoice?',
      body: 'The advertiser has already paid for this notice. If you wish to refund a full or partial amount on the invoice, please specify below. If you void the invoice, Column will refund the entire amount of the invoice.',
      refundDisabled,
      voidAndRefundDisabled: false,
      performRefund: async (ctx: PerformRefundContext) => {
        const { args, reason, totalAmountInCents } = ctx;
        const { error } = await api.safePostWithParams<'payments/:id/refund'>(
          `payments/${args.entity.id}/refund`,
          {
            refundReason: reason,
            refundAmount: String(totalAmountInCents / 100),
            isInvoiceCancellation: false
          }
        );
        if (error) {
          logAndCaptureException(
            ColumnService.PAYMENTS,
            error,
            'Failed to refund notice invoice',
            { noticeId: args.entity.id }
          );
          throw error;
        }
      },
      performVoidAndRefund: async (ctx: PerformRefundContext) => {
        const { args, reason } = ctx;
        await api.safePost('payments/cancel-invoice', {
          noticeId: args.entity.id,
          voidReason: reason || undefined
        });
      }
    };
  },
  buildDisplaySubtitle({ entity }) {
    if (entity.kind !== 'notice') return '';
    const noticeTitle = entity.referenceId;
    const base = `Paid invoice for notice ${entity.id}`;
    return noticeTitle ? `${base} ("${noticeTitle}")` : base;
  }
};
